import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type {
  Balance,
  Category,
  RecurringAllowance,
  Transaction,
  UnseenMoney,
} from "../api/types";
import { useAuth, useErrorHandler } from "../auth/AuthContext";
import { BalanceView, bucketCents } from "../components/BalanceView";
import { MoneyArrived } from "../components/MoneyArrived";
import { SpendMoney } from "../components/SpendMoney";
import { TransferMoney } from "../components/TransferMoney";
import { SplitChooser } from "../components/SplitChooser";
import { TransactionList } from "../components/TransactionList";
import { ErrorMessage } from "../components/common";
import { usePoll } from "../lib/usePoll";

const PAGE_SIZE = 25;

/** How often to check for new money while the tab is visible. */
const POLL_MS = 15000;

/**
 * A child's read-only view of their own money.
 *
 * There is no editing UI here, but that is presentation only — the server
 * refuses a child's write on every route regardless of what this page renders.
 *
 * The page keeps itself current: it re-reads the balance and recent ledger on a
 * timer, so allowance paid by the server's background runner appears without a
 * refresh, and arriving money is celebrated rather than silently appended.
 *
 * What counts as "arriving" is the server's call, not this page's. It used to
 * be worked out here by diffing the newest page against transaction IDs held in
 * memory, which meant a fresh login detected nothing at all: log in the morning
 * after the overnight allowance ran and the celebration had already been missed
 * by a marker that started empty. The watermark now lives on the child's user
 * row, so it survives a logout and follows them to another device.
 */
export function ChildDashboard() {
  const { user } = useAuth();
  const toMessage = useErrorHandler();

  const [balance, setBalance] = useState<Balance | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [celebration, setCelebration] = useState<{
    unseen: UnseenMoney;
    balance: Balance;
  } | null>(null);
  const [allowances, setAllowances] = useState<RecurringAllowance[]>([]);
  const [savePercent, setSavePercent] = useState(user?.savePercent ?? 0);
  const [spending, setSpending] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const childId = user?.id;

  /** The Spend bucket's current balance — what a child may spend from. */
  const spendBalanceCents = bucketCents(balance, "spend");

  /** The Save bucket's balance, the other end of a transfer. */
  const saveBalanceCents = bucketCents(balance, "save");

  /** The next allowance amount, for previewing what a split would do to it. */
  const nextAllowanceCents =
    allowances.find((a) => a.active)?.amountCents ?? null;

  // Whether a celebration is already on screen. A ref as well as state because
  // refresh reads it without wanting to be re-created every time it changes —
  // a new refresh identity would restart the poll timer.
  const celebrating = useRef(false);
  celebrating.current = celebration !== null;

  // The last watermark this tab dismissed, as epoch milliseconds. The server is
  // the authority on what is unseen, but it does not learn about a dismissal
  // until the acknowledgement lands — and if that request fails there is no next
  // attempt. Without this the same popup would return every fifteen seconds
  // instead of, at worst, at the next login.
  const dismissedThrough = useRef(0);

  /**
   * Fetches the current balance, newest page, and anything the child has not
   * been shown yet.
   *
   * Every load asks about new money, including the first: that is the whole
   * point of a durable watermark. It is skipped only while a celebration is
   * already up, since re-setting the same one would restart its auto-dismiss
   * timer and leave it on screen indefinitely.
   */
  const refresh = useCallback(async () => {
    if (!childId) return;
    try {
      const [bal, cats, page, rules, unseen] = await Promise.all([
        api.balance(childId),
        api.listCategories(childId),
        api.listTransactions(childId, { limit: PAGE_SIZE }),
        api.listAllowances(childId),
        celebrating.current ? null : api.newMoney(),
      ]);

      // Parsed, not compared as text: the server renders timestamps as RFC 3339
      // with trailing zeros trimmed, so ".78Z" and ".789Z" do not sort the way
      // they read.
      if (
        unseen &&
        unseen.transactions.length > 0 &&
        Date.parse(unseen.throughAt) > dismissedThrough.current
      ) {
        setCelebration({ unseen, balance: bal });
      }

      setBalance(bal);
      setCategories(cats);
      setTransactions(page.transactions);
      setNextCursor(page.nextCursor);
      setAllowances(rules.allowances);
      setError("");
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
  }, [childId, toMessage]);

  /**
   * Closes the celebration and tells the server it was seen, so the same money
   * is not celebrated again on the next login.
   *
   * The watermark comes from the response that was displayed rather than from
   * the clock: money that arrived while the popup was up has not been seen, and
   * acknowledging "everything until now" would swallow it. A failed
   * acknowledgement is deliberately silent — the cost is one repeated
   * celebration, which is not worth an error message to a child.
   */
  const dismissCelebration = useCallback(() => {
    const seen = celebration?.unseen.throughAt;
    setCelebration(null);
    if (!seen) return;
    dismissedThrough.current = Date.parse(seen);
    void api.markMoneySeen(seen).catch(() => {});
  }, [celebration]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  usePoll(() => refresh(), POLL_MS);

  async function loadMore() {
    if (!nextCursor || !childId) return;
    try {
      const page = await api.listTransactions(childId, {
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setTransactions((prev) => [...prev, ...page.transactions]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(toMessage(err));
    }
  }

  return (
    <div className="page">
      <ErrorMessage>{error}</ErrorMessage>

      <div className="child-greeting">
        <span className="avatar avatar--lg">{user?.avatar || "🙂"}</span>
        <div>
          <h1>Hi {user?.displayName}</h1>
          <p className="muted">Here's how your money is doing.</p>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          {balance ? (
            <BalanceView balance={balance} label="Total:" />
          ) : null}

          <div className="child-actions">
            <button
              type="button"
              className="btn btn--primary btn--spend"
              onClick={() => setSpending(true)}
              disabled={spendBalanceCents <= 0}
            >
              {spendBalanceCents > 0
                ? "Spend some money"
                : "No spending money yet"}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--spend"
              onClick={() => setTransferring(true)}
              disabled={spendBalanceCents <= 0 && saveBalanceCents <= 0}
            >
              Move money
            </button>
          </div>

          {childId ? (
            <div className="card">
              <SplitChooser
                childId={childId}
                savePercent={savePercent}
                allowanceCents={nextAllowanceCents}
                onSaved={setSavePercent}
              />
            </div>
          ) : null}

          <div className="card card--flush">
            <div className="section-head">
              <h2>What happened</h2>
            </div>

            <TransactionList
              transactions={transactions}
              categories={categories}
              canEdit={false}
              emptyMessage="Nothing yet — your first allowance will show up here."
            />

            {nextCursor ? (
              <div
                className="row"
                style={{ padding: "var(--gap) var(--gap-lg)" }}
              >
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={loadMore}
                >
                  Show older
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}

      {spending && childId ? (
        <SpendMoney
          childId={childId}
          spendBalanceCents={spendBalanceCents}
          onClose={() => setSpending(false)}
          onSpent={() => refresh()}
        />
      ) : null}

      {transferring && childId ? (
        <TransferMoney
          childId={childId}
          spendCents={spendBalanceCents}
          saveCents={saveBalanceCents}
          onClose={() => setTransferring(false)}
          onTransferred={() => refresh()}
        />
      ) : null}

      {celebration ? (
        <MoneyArrived
          amountCents={celebration.unseen.totalCents}
          transactions={celebration.unseen.transactions}
          categories={categories}
          balance={celebration.balance}
          onDismiss={dismissCelebration}
        />
      ) : null}
    </div>
  );
}
